/**
 * Utility functions for consistent date handling across the application
 * to avoid timezone issues when working with date-only values.
 */

/** Matches ISO/datetime strings: YYYY-MM-DD[ T]HH:mm (DB may return space instead of T) */
const DB_DATETIME =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?/;
/** True if string has explicit timezone (Z or ±HH:mm) */
function hasTimezoneInfo(s: string): boolean {
  const t = s.trim();
  return t.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(t);
}

/**
 * Converts a timestamp value from the DB driver (Date or string) to ISO 8601 string.
 * Handles both node-pg (Date) and Vercel Postgres (string) return types.
 * CRITICAL: Strings without timezone are treated as UTC (DB stores timestamptz).
 * Otherwise JS parses them as server local time, causing 4-hour offsets.
 * @param val - Raw value from database (Date, string, or null/undefined)
 * @returns ISO string with Z (e.g. "2026-03-09T16:07:00.000Z") or undefined
 */
export function toIsoOrUndefined(val: unknown): string | undefined {
  if (val == null) return undefined;
  if (val instanceof Date && !isNaN(val.getTime())) return val.toISOString();
  if (typeof val === "string" && DB_DATETIME.test(val)) {
    let normalized = val.trim();
    if (/^\d{4}-\d{2}-\d{2} \d/.test(normalized)) {
      normalized = normalized.replace(" ", "T");
    }
    if (!hasTimezoneInfo(val)) {
      normalized = normalized + (normalized.endsWith("Z") ? "" : "Z");
    }
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }
  return undefined;
}

/**
 * Resolves a stored invoice/trip-style value to a Date for calendar display.
 * For ISO strings (including UTC midnight from DB), uses the YYYY-MM-DD part with
 * createLocalDate so the day does not shift by viewer timezone.
 */
function resolveCalendarDate(
  dateValue: string | Date | null | undefined
): Date | null {
  if (dateValue == null) return null;
  try {
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return null;
      const iso = dateValue.toISOString();
      if (iso.match(/^\d{4}-\d{2}-\d{2}T/)) {
        const dateOnly = iso.split("T")[0];
        const parsedDate = createLocalDate(dateOnly);
        return !parsedDate || isNaN(parsedDate.getTime()) ? null : parsedDate;
      }
      return null;
    }

    if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parsedDate = createLocalDate(dateValue);
      return !parsedDate || isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) {
      const dateOnly = dateValue.split("T")[0];
      const parsedDate = createLocalDate(dateOnly);
      return !parsedDate || isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

export type CalendarDateDisplayMonthStyle = "short" | "long";

/**
 * Formats a calendar date for display using the same parsing as formatDateForDisplay.
 * Use short month to match compact invoice cards (e.g. "Oct 1, 2026").
 */
export function formatCalendarDateForDisplay(
  dateValue: string | Date | null | undefined,
  options?: { month?: CalendarDateDisplayMonthStyle }
): string {
  const date = resolveCalendarDate(dateValue);
  if (!date) return "N/A";
  const month = options?.month === "short" ? "short" : "long";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month,
    day: "numeric",
  });
}

/**
 * Standardized date formatting function for consistent display across the application
 * Formats dates as "Month Day, Year" (e.g., "July 10, 2025")
 * @param dateValue - The date value (string, Date object, or null/undefined)
 * @returns Formatted date string in "Month Day, Year" format, or "N/A" if invalid
 */
export const formatDateForDisplay = (
  dateValue: string | Date | null | undefined
): string => {
  return formatCalendarDateForDisplay(dateValue, { month: "long" });
};

/** Matches ISO-like strings with optional ms and timezone: YYYY-MM-DDTHH:mm[:ss][.sss][Z|±HH:mm] */
const ISO_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?/;
/** True if string has explicit UTC timezone (Z or +00:00 etc.) */
function hasTimezone(s: string): boolean {
  return s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s.trim());
}

/**
 * Formats a date-time value for display (same style as Expires in Share Invoice dialog):
 * "March 14, 2026 at 12:14 PM"
 * Uses the user's local timezone (converts from UTC if the value is stored as ISO string).
 * Treats any ISO-like string without timezone as UTC.
 * @param dateValue - The date value (string, Date object, or null/undefined)
 * @returns Formatted date-time string, or "N/A" if invalid
 */
export const formatDateTimeForDisplay = (
  dateValue: string | Date | null | undefined
): string => {
  if (!dateValue) return "N/A";
  try {
    let isoString: string;
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return "N/A";
      isoString = dateValue.toISOString();
    } else if (typeof dateValue === "string" && ISO_DATE_TIME.test(dateValue)) {
      // Normalize: treat strings without TZ as UTC
      isoString = hasTimezone(dateValue) ? dateValue : `${dateValue}Z`;
    } else {
      isoString = String(dateValue);
    }
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "N/A";
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: localTimeZone,
    });
  } catch {
    return "N/A";
  }
};

/**
 * Parses a date-only string (YYYY-MM-DD) without timezone conversion issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object with the date components, or null if invalid
 */
const parseDateOnlyString = (dateString: string): Date | null => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return null;
  }

  try {
    const [year, month, day] = dateString.split("-").map(Number);
    // Use UTC methods to avoid timezone conversion
    return new Date(Date.UTC(year, month - 1, day));
  } catch {
    return null;
  }
};

/**
 * Formats dates in dd/mm/yyyy format for specific use cases like payment history
 * Uses timezone-safe parsing for date-only strings to prevent day shifts
 * @param dateValue - The date value (string, Date object, or null/undefined)
 * @returns Formatted date string in dd/mm/yyyy format, or "N/A" if invalid
 */
export const formatDateDDMMYYYY = (
  dateValue: string | Date | null | undefined
): string => {
  if (!dateValue) return "N/A";
  try {
    let date: Date;

    // Handle date-only strings (YYYY-MM-DD) with timezone-safe parsing
    if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parsedDate = parseDateOnlyString(dateValue);
      if (!parsedDate || isNaN(parsedDate.getTime())) return "N/A";
      date = parsedDate;
    } else if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) {
      // ISO string with time component - use as is but extract date part safely
      const dateOnly = dateValue.split("T")[0];
      const parsedDate = parseDateOnlyString(dateOnly);
      if (!parsedDate || isNaN(parsedDate.getTime())) return "N/A";
      date = parsedDate;
    } else {
      // Date object or other string format - use standard parsing
      date = new Date(dateValue);
      if (isNaN(date.getTime())) return "N/A";
    }

    // Use UTC methods to extract date components consistently
    const day = date.getUTCDate().toString().padStart(2, "0");
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return "N/A";
  }
};

/**
 * Formats a date value for display in an HTML date input (YYYY-MM-DD format)
 * @param dateValue - The date value (string, Date object, or null/undefined)
 * @returns Formatted date string in YYYY-MM-DD format, or empty string if invalid
 */
export const formatDateForInput = (
  dateValue: string | Date | null | undefined
): string => {
  if (!dateValue) return "";

  try {
    // If it's already a YYYY-MM-DD string, return as is
    if (
      typeof dateValue === "string" &&
      dateValue.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      return dateValue;
    }

    // If it's an ISO string, extract just the date part
    if (typeof dateValue === "string" && dateValue.includes("T")) {
      return dateValue.split("T")[0];
    }

    // For Date objects or other formats, use UTC methods to avoid timezone issues
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

/**
 * Creates a Date object from a YYYY-MM-DD string without timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the date in local timezone
 */
export const createLocalDate = (dateString: string): Date | null => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return null;
  }

  try {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  } catch {
    return null;
  }
};

/**
 * Formats a date value for API submission, handling timezone issues
 * @param value - The date value to format
 * @returns Date object or null
 */
export const formatDateForAPI = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;

  if (typeof value === "string") {
    // Handle YYYY-MM-DD format (date-only strings) to avoid timezone issues
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return createLocalDate(value);
    }
    return new Date(value); // Convert to Date object for other formats
  }

  if (value instanceof Date) return value; // Already a Date object
  return null;
};

/**
 * Normalizes a date value for DB storage or API response.
 * Returns YYYY-MM-DD string or null (for empty/invalid).
 */
export const formatDateOnlyForDB = (
  value: string | Date | null | undefined
): string | null => {
  const normalized = formatDateForInput(value);
  return normalized || null;
};

/**
 * Validates if a date string is in the correct format
 * @param dateString - The date string to validate
 * @returns True if the date string is valid
 */
export const isValidDateString = (dateString: string): boolean => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return false;
  }

  const date = createLocalDate(dateString);
  return date !== null && !isNaN(date.getTime());
};

export function toIsoStringSafe(
  value: string | Date | undefined | null
): string {
  if (!value) return "";

  // If it's already a Date object
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      console.warn("Invalid Date object:", value);
      return "";
    }
    return value.toISOString();
  }

  // If it's a string, try to parse it
  if (typeof value === "string") {
    // Handle empty or whitespace strings
    if (!value.trim()) return "";

    // If it's already an ISO string, return as is
    if (value.includes("T")) {
      return value;
    }

    // If it's a YYYY-MM-DD format, create date in UTC to avoid timezone issues
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = value.split("-").map(Number);
      const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
      return date.toISOString();
    }

    // For other formats, try to parse the date
    const parsedDate = new Date(value);
    if (isNaN(parsedDate.getTime())) {
      console.warn("Invalid date string:", value);
      return "";
    }
    return parsedDate.toISOString();
  }

  // For any other type, return empty string
  console.warn("Unexpected date value type:", typeof value, value);
  return "";
}
